import { shallowReactive } from 'vue'
import type {
  BestBidOfferModel,
  BestBidOfferTickEntry,
  OrderSide,
  PositionSide
} from '@trading/core'
import type { TrendDirection } from '~/stores/OrderBook'

type StoreForDetectDirection = {
  lastPrice: number | null
  lastPriceTime: number | null
  currentPrice: number | null
  direction: TrendDirection | null
}

export type BestBidOfferQuote = {
  bid: number | null
  ask: number | null
  last: number | null
  spread: number | null
  /** время тика с сервера */
  ts: number
  direction: TrendDirection
  /** локальное время применения тика в стор */
  updatedAt: number
}

type SubscriptionId = number

const BEST_BID_OFFER_DETECT_DIRECTION_INTERVAL_MS = 1000

export const useBestBidOfferStore = defineStore('bestBidOffer', () => {
  /** последний «сырой» тик по инструменту, как его присылает сервер */
  const ticksBySymbol = shallowReactive<Record<string, BestBidOfferTickEntry>>({})
  /** тот же тик, приведенный к числам — для расчетов и таблиц */
  const quotesBySymbol = shallowReactive<Record<string, BestBidOfferQuote>>({})

  /** symbols, на которые сейчас оформлена подписка канала */
  const subscribedSymbols = new Set<string>()
  const detectDirectionStore = shallowReactive<Record<string, StoreForDetectDirection>>({})

  const subscriptions = new Map<SubscriptionId, Set<string>>()
  let nextSubscriptionId = 0
  let reconciliationPromise: Promise<void> = Promise.resolve()

  function getEffectiveSymbols() {
    const result = new Set<string>()

    for (const symbols of subscriptions.values()) {
      for (const symbol of symbols) {
        result.add(symbol)
      }
    }

    return result
  }

  /**
   * bbo — канальная подписка: сервер шлет один поток `{ ticks: [...] }` сразу по
   * всем symbols, на которые мы подписаны, а symbols добавляются/убираются
   * отдельными кадрами subscribe/unsubscribe.
   *
   * Значит обработчик сообщений канала нужен ровно один на весь стор и не
   * зависит от состава подписки: он ставится при первой подписке и снимается
   * только в unsubscribeFromAllSymbols.
   */
  let removeChannelHandler: (() => void) | null = null
  let channelHandlerPromise: Promise<void> | null = null

  const tickBuffer = new Map<string, BestBidOfferTickEntry>()
  let flushFrame: number | null = null

  function getWsBestBidOfferService() {
    return useNuxtApp().$wsBestBidOfferService
  }

  function normalizeSymbol(symbol: string) {
    return symbol ? symbol.trim() : ''
  }

  function parsePrice(value: string | number | undefined | null): number | null {
    if (value === undefined || value === null || value === '') {
      return null
    }

    const parsed = Number.parseFloat(String(value))

    return Number.isFinite(parsed) ? parsed : null
  }

  function extractQuote(tick: BestBidOfferTickEntry): BestBidOfferQuote {
    const bid = parsePrice(tick.bid) ?? parsePrice(tick.last)
    const ask = parsePrice(tick.ask) ?? parsePrice(tick.last)

    return {
      bid,
      ask,
      last: parsePrice(tick.last),
      spread: bid !== null && ask !== null ? ask - bid : null,
      ts: tick.ts,
      direction: detectDirection(bid, tick.symbol),
      updatedAt: Date.now()
    }
  }

  function scheduleFlush() {
    if (flushFrame !== null) {
      return
    }

    flushFrame = requestAnimationFrame(() => {
      flushFrame = null
      flushBufferedTicks()
    })
  }

  function queueBestBidOfferUpdate(symbol: string, tick: BestBidOfferTickEntry) {
    tickBuffer.set(normalizeSymbol(symbol), tick)
    scheduleFlush()
  }

  function flushBufferedTicks() {
    for (const [symbol, tick] of tickBuffer) {
      ticksBySymbol[symbol] = tick
      quotesBySymbol[symbol] = extractQuote(tick)
    }

    tickBuffer.clear()
  }

  /**
   * Одно сообщение канала содержит тики по разным инструментам,
   * поэтому раскладываем их по symbol из самого тика, а не по symbol подписки.
   */
  function handleBestBidOfferMessage(message: BestBidOfferModel) {
    if (!message?.ticks?.length) {
      return
    }

    for (const tick of message.ticks) {
      if (!tick?.symbol) {
        continue
      }

      const normalizedSymbol = normalizeSymbol(tick.symbol)

      // тики по symbol, с которого уже отписались (окно между unsubscribe и ответом сервера)
      if (!subscribedSymbols.has(normalizedSymbol)) {
        continue
      }

      queueBestBidOfferUpdate(normalizedSymbol, tick)
    }
  }

  function getTick(symbol: string): BestBidOfferTickEntry | null {
    return ticksBySymbol[normalizeSymbol(symbol)] ?? null
  }

  function detectDirection(bid: number | null, symbol: string): TrendDirection {
    let direction: TrendDirection = 'neutral'
    if (!detectDirectionStore[symbol]) {
      detectDirectionStore[symbol] = {
        lastPrice: null,
        lastPriceTime: null,
        currentPrice: null,
        direction: null
      }
    }
    const lastPrice = detectDirectionStore[symbol]?.lastPrice
    const lastPriceTime = detectDirectionStore[symbol]?.lastPriceTime
    if (
      lastPriceTime === null ||
      Date.now() - lastPriceTime >= BEST_BID_OFFER_DETECT_DIRECTION_INTERVAL_MS
    ) {
      if (!bid) {
        detectDirectionStore[symbol].direction = 'neutral'
        return detectDirectionStore[symbol].direction
      }
      if (lastPrice && bid > lastPrice) {
        direction = 'up'
      } else if (lastPrice && bid < lastPrice) {
        direction = 'down'
      } else {
        direction = detectDirectionStore[symbol].direction || 'neutral'
      }
      detectDirectionStore[symbol].lastPrice = bid
      detectDirectionStore[symbol].lastPriceTime = Date.now()
    } else {
      direction = detectDirectionStore[symbol].direction || 'neutral'
    }
    detectDirectionStore[symbol].direction = direction
    return direction
  }

  function getQuote(symbol: string): BestBidOfferQuote | null {
    const normalizedSymbol = normalizeSymbol(symbol)
    const alternateSymbol = normalizedSymbol.startsWith('#')
      ? normalizedSymbol.slice(1)
      : `#${normalizedSymbol}`

    return quotesBySymbol[normalizedSymbol] ?? quotesBySymbol[alternateSymbol] ?? null
  }

  function getBestAsk(symbol: string): number | null {
    return getQuote(symbol)?.ask ?? null
  }

  function getBestBid(symbol: string): number | null {
    return getQuote(symbol)?.bid ?? null
  }

  function getLastPrice(symbol: string): number | null {
    return getQuote(symbol)?.last ?? null
  }

  function getSpread(symbol: string): number | null {
    return getQuote(symbol)?.spread ?? null
  }

  function getMarketPrice(symbol: string, side: OrderSide): number | null {
    return side.toUpperCase() === 'BUY' ? getBestAsk(symbol) : getBestBid(symbol)
  }

  function getClosePrice(symbol: string, positionSide: PositionSide): number | null {
    return positionSide.toUpperCase() === 'LONG' ? getBestBid(symbol) : getBestAsk(symbol)
  }

  function normalizeSymbols(symbolOrSymbols: string | string[]): string[] {
    const symbols = Array.isArray(symbolOrSymbols) ? symbolOrSymbols : [symbolOrSymbols]

    return [...new Set(symbols.map(normalizeSymbol).filter(Boolean))]
  }

  async function ensureChannelHandler() {
    if (removeChannelHandler) {
      return
    }

    if (channelHandlerPromise) {
      return channelHandlerPromise
    }

    channelHandlerPromise = getWsBestBidOfferService()
      .onBestBidOfferUpdate(handleBestBidOfferMessage)
      .then((removeHandler) => {
        removeChannelHandler = removeHandler ?? null
      })
      .finally(() => {
        channelHandlerPromise = null
      })

    return channelHandlerPromise
  }

  /** Убирает symbol из состава подписки; данные оставляем, чтобы UI не мигал */
  function forgetSymbol(symbol: string) {
    subscribedSymbols.delete(symbol)

    // тик, который не успел примениться, для отписанного symbol уже не нужен
    tickBuffer.delete(symbol)
  }

  /** Адресно добавляет symbols к текущей подписке канала */
  async function subscribeToSymbols(symbolOrSymbols: string | string[]) {
    const symbolsToSubscribe = normalizeSymbols(symbolOrSymbols).filter(
      (symbol) => !subscribedSymbols.has(symbol)
    )

    if (!symbolsToSubscribe.length) {
      return
    }

    symbolsToSubscribe.forEach((symbol) => subscribedSymbols.add(symbol))

    try {
      // обработчик должен стоять до кадра subscribe, иначе первые тики потеряются
      await ensureChannelHandler()

      await getWsBestBidOfferService().subscribeToSymbols(symbolsToSubscribe)
    } catch (error) {
      // не считаем подписанным то, на что подписаться не удалось
      symbolsToSubscribe.forEach(forgetSymbol)
      throw error
    }
  }

  /** Снимает подписку только с переданных symbols, остальные не трогает */
  async function unsubscribeFromSymbols(symbolOrSymbols: string | string[]) {
    const symbols = normalizeSymbols(symbolOrSymbols).filter((symbol) =>
      subscribedSymbols.has(symbol)
    )

    if (!symbols.length) {
      return
    }

    symbols.forEach(forgetSymbol)

    await getWsBestBidOfferService().unsubscribeFromSymbols(symbols)
  }

  /** Снимает подписку целиком вместе с общим обработчиком канала
   *  Использовать с осторожностью только если явно хотим отписаться от всего, к примеру при разлогинивании
   *  Для обычных случаев лучше использовать createSubscription */
  async function unsubscribeFromAllSymbols() {
    const symbols = [...subscribedSymbols]

    symbols.forEach(forgetSymbol)

    // ждем незавершенную установку обработчика, иначе снимать было бы нечего
    await channelHandlerPromise?.catch(() => undefined)

    removeChannelHandler?.()
    removeChannelHandler = null

    if (!symbols.length) {
      return
    }

    await getWsBestBidOfferService().unsubscribeFromSymbols(symbols)
  }

  function getSubscribedSymbols(): string[] {
    return [...subscribedSymbols]
  }

  function resetBestBidOffer(symbol: string) {
    const normalizedSymbol = normalizeSymbol(symbol)

    tickBuffer.delete(normalizedSymbol)

    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete ticksBySymbol[normalizedSymbol]

    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete quotesBySymbol[normalizedSymbol]
  }

  function resetAllBestBidOffers() {
    tickBuffer.clear()

    for (const symbol of Object.keys(ticksBySymbol)) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete ticksBySymbol[symbol]
    }

    for (const symbol of Object.keys(quotesBySymbol)) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete quotesBySymbol[symbol]
    }
  }

  function createSubscription() {
    const id = ++nextSubscriptionId
    let released = false

    function setSymbols(symbolOrSymbols: string | string[]) {
      if (released || !symbolOrSymbols) return

      subscriptions.set(id, new Set(normalizeSymbols(symbolOrSymbols)))
      scheduleReconciliation()
    }

    function release() {
      if (released) return

      released = true
      subscriptions.delete(id)
      scheduleReconciliation()
    }

    return {
      setSymbols,
      release
    }
  }

  async function releaseAll() {
    subscriptions.clear()
    await scheduleReconciliation().catch(() => undefined)
    await unsubscribeFromAllSymbols()
  }

  async function reconcileSubscriptions() {
    const nextSymbols = getEffectiveSymbols()

    const symbolsToSubscribe = [...nextSymbols].filter((symbol) => !subscribedSymbols.has(symbol))

    const symbolsToUnsubscribe = [...subscribedSymbols].filter((symbol) => !nextSymbols.has(symbol))

    await subscribeToSymbols(symbolsToSubscribe)
    await unsubscribeFromSymbols(symbolsToUnsubscribe)
  }

  function scheduleReconciliation(): Promise<void> {
    // Последовательное выполнение не позволяет запоздавшему subscribe обогнать
    // более новый unsubscribe при быстрых изменениях списка инструментов.
    reconciliationPromise = reconciliationPromise.then(
      reconcileSubscriptions,
      reconcileSubscriptions
    )

    // Вызовы из Vue watchers fire-and-forget; обработчик не дает отклоненному
    // promise превратиться в unhandled rejection. Следующий запуск повторит reconcile.
    void reconciliationPromise.catch(() => undefined)

    return reconciliationPromise
  }

  return {
    ticksBySymbol,
    quotesBySymbol,

    getTick,
    getQuote,
    getBestAsk,
    getBestBid,
    getLastPrice,
    getSpread,
    getMarketPrice,
    getClosePrice,

    createSubscription,

    unsubscribeFromAllSymbols,
    getSubscribedSymbols,

    resetBestBidOffer,
    resetAllBestBidOffers,

    releaseAll
  }
})
